import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { Icon, USER_INTERFACE_CROSS_CANCEL } from '@bwoty-web/styleguide';
import Header from './components/header/header';
import Footer from './components/footer/footer';
import { getBackgroundImageStyling } from './utils/imageUtils';
import RoutesContainer from './routesContainer';
import { activeFilterStepPropType, filtersPropType } from './utils/propTypes';
import { logEventToDataLayer } from './utils/tracking';
import { filterSteps, filterStepsTypes, destinationTraits, destinationTraitNames, baseRoutes } from './appSettings';
import { appInit, handleRouteLoad } from './redux/actions/siteSettingsActions';
import { getTotalHits } from './redux/actions/resultsActions';
import { useIsDesktop } from './hooks/useIsDesktop';
import './styles/vacationFinderBase.scss';

const VacationFinderBase = ({
  isServer,
  onInitApp,
  onRouteLoad,
  onGetTotalHits,
  filters,
  baseUrl,
  cdnBaseUrl,
  imageDomain,
  preferenceBackgroundImage,
  locationBackgroundImage,
  temperatureBackgroundImage,
  flightTimeBackgroundImage,
  activeFilterStep,
}) => {
  const [ isClosed, setIsClosed] = useState(false);

  const { 
    selectedSecondaryClassifications,
    selectedHotelProducts,
    selectedConceptHotels,
    selectedLocations,
    selectedFlightTime,
    selectedTemperature,
    selectedTemperaturePeriod,
  } = filters;

  const location = useLocation();

  /*
    We need to keep track of initRoute to allow a url to be shared in its current state
  */
  useEffect(() => {
    if (!isServer) {
      // init fetch of classifications and set init state from url query params
      onInitApp();
      onRouteLoad(location.pathname);
    }
  }, []);

  // // Prevent scrolling in background when modal is open
  useEffect(() => {
    if (!isClosed) {
      // Prevent scrolling in background when modal is open
      document.querySelector('body').style.overflow = 'hidden';
      document.querySelector('html').style.overflow = 'hidden'; // Fix for iOS
    } else {
      // and restore it when it's closed
      document.querySelector('body').style.overflow = 'auto';
      document.querySelector('html').style.overflow = 'auto';
    }
  }, [isClosed]);

  /*
    Hook up to the location.pathname change. Whenever the app navigates
    We run a handler that keeps the progressbar up to date and if it
    should show / be hidden
  */
  useEffect(() => {
    onRouteLoad(location.pathname);
  }, [location.pathname])

  useEffect(() => {
    // Whenever one of the listed filters are updated - trigger a new request to get total of hits
    onGetTotalHits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSecondaryClassifications,
    selectedHotelProducts,
    selectedConceptHotels,
    selectedLocations,
    selectedFlightTime,
    selectedTemperature,
    selectedTemperaturePeriod,
  ])
  const isDesktop = useIsDesktop();

  const onClose = () => {
    logEventToDataLayer({
      filterSteps,
      filterStepsTypes,
      filters,
      activeFilterStep,
      destinationTraits,
      destinationTraitNames,
      eventActionClickType: 'Close Window',
    });
    setIsClosed(true);
  };

  
  const overlayClasses = classnames({
    'vf-overlay': true,
    'vf-overlay-disabled': isClosed,
  });

  const closeButtonClasses = classnames({
    'vf-close-button': true,
    'vf-close-button-white': location.pathname === `/${baseRoutes.START}`,
  });

  const isTraitsDetailsView = location.pathname === `/${filterStepsTypes.DESTINATION_TRAITS_DETAILS}`;
  const isTraitsSelectionView = location.pathname === `/${filterStepsTypes.DESTINATION_TRAITS_SELECTION}`;
  const hasBackgroundImage = isTraitsDetailsView || isTraitsSelectionView;

  const getBackgroundStyling = () => {
    if (isTraitsSelectionView) {
      return getBackgroundImageStyling('vf-bg', imageDomain, preferenceBackgroundImage);
    }
    if (isTraitsDetailsView) {
      switch (filters.selectedPreference) {
        case destinationTraits.LOCATION:
          return getBackgroundImageStyling('vf-bg', imageDomain, locationBackgroundImage);
        case destinationTraits.FLIGHT_TIME:
          return getBackgroundImageStyling('vf-bg', imageDomain, flightTimeBackgroundImage);
        case destinationTraits.TEMPERATURE:
          return getBackgroundImageStyling('vf-bg', imageDomain, temperatureBackgroundImage);
        default:
          break;
      }
    }
    return '';
  };

  return (
    <div className={overlayClasses}>

      <div className={closeButtonClasses}>
        <Icon
          path={USER_INTERFACE_CROSS_CANCEL}
          onClick={() => onClose()}
          role="button"
          size={isDesktop ? "s" : 'xs'}
        />
      </div>
      <style scoped>
        {/* injecting style this way to be able to set different media urls and not have refetching on resizing */}
        {getBackgroundStyling()}
      </style>
      <main
        className={`vf-main vf-bg ${hasBackgroundImage ? 'vf-image-bg' : 'vf-striped-bg'}  ${isTraitsDetailsView ? 'vf-in-filter-flow' : ''}`}
        style={!hasBackgroundImage ? { backgroundImage: `url("${cdnBaseUrl || baseUrl}/static/images/wave-bg.svg")` } : null}
      >
        <Header />
        
        {!isServer && <RoutesContainer />}
        
        <Footer />
      </main>
    </div>
  )
}

VacationFinderBase.propTypes = {
  isServer: PropTypes.bool.isRequired,
  onInitApp: PropTypes.func.isRequired,
  onRouteLoad: PropTypes.func.isRequired,
  onGetTotalHits: PropTypes.func.isRequired,
  filters: PropTypes.shape(filtersPropType).isRequired,
  baseUrl: PropTypes.string.isRequired,
  cdnBaseUrl: PropTypes.string,
  imageDomain: PropTypes.string,
  preferenceBackgroundImage: PropTypes.shape({}),
  locationBackgroundImage: PropTypes.shape({}),
  temperatureBackgroundImage: PropTypes.shape({}),
  flightTimeBackgroundImage: PropTypes.shape({}),
  activeFilterStep: PropTypes.shape(activeFilterStepPropType).isRequired,
};

VacationFinderBase.defaultProps = {
  cdnBaseUrl: '',
  imageDomain: '',
  preferenceBackgroundImage: null,
  locationBackgroundImage: null,
  temperatureBackgroundImage: null,
  flightTimeBackgroundImage: null,
}

const mapStateToProps = (state) => {
  return {
    filters: state.siteSettings.filters,
    baseUrl: state.siteSettings.baseUrl,
    cdnBaseUrl: state.siteSettings.cdnBaseUrl,
    imageDomain: state.siteSettings.imageUrl,
    preferenceBackgroundImage: state.attributeCollection.preferencePickerBackgroundImage.image,
    flightTimeBackgroundImage: state.attributeCollection.flightTimePickerBackgroundImage.image,
    temperatureBackgroundImage: state.attributeCollection.temperaturePickerBackgroundImage.image,
    locationBackgroundImage: state.attributeCollection.locationPickerBackgroundImage.image,
    activeFilterStep: state.siteSettings.activeFilterStep,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onInitApp: () => {
      dispatch(appInit());
    },
    onRouteLoad: (pathname) => {
      dispatch(handleRouteLoad(pathname));
    },
    onGetTotalHits: () => {
      dispatch(getTotalHits());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(VacationFinderBase)